import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { CognitoUserPool } from "amazon-cognito-identity-js";

// Cognito configuration - using the same config from Login.js
const COGNITO_CONFIG = {
  userPoolId: "us-east-1_fc1omeTGh",
  clientId: "54fiucjkk95o6gna3l5l3dnjuo",
  region: "us-east-1",
};

// Create the Cognito User Pool object
const userPool = new CognitoUserPool({
  UserPoolId: COGNITO_CONFIG.userPoolId,
  ClientId: COGNITO_CONFIG.clientId,
});

// Set up the GraphQL API endpoint
const httpLink = createHttpLink({
  uri:
    process.env.REACT_APP_GRAPHQL_API_URL ||
    "https://nkeefgsaz2.execute-api.us-east-1.amazonaws.com/prod/graphql",
});

// Error handling link
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      );
    });
  }
  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
  }
});

// Authentication link to add authorization headers
const authLink = setContext(async (_, { headers }) => {
  try {
    // Get the authentication token from session storage (same approach as Login.js)
    const storedToken = sessionStorage.getItem("cognitoIdToken");

    if (storedToken) {
      return {
        headers: {
          ...headers,
          Authorization: `Bearer ${storedToken}`,
        },
      };
    }

    return { headers };
  } catch (error) {
    console.log("Not authenticated", error);
    return {
      headers,
    };
  }
});

// Create Apollo Client
const client = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "network-only",
      errorPolicy: "all",
    },
    query: {
      fetchPolicy: "network-only",
      errorPolicy: "all",
    },
    mutate: {
      errorPolicy: "all",
    },
  },
});

// User profile mutations
export const UPDATE_USER_PROFILE = `
  mutation UpdateUserProfile($input: UserProfileInput!) {
    updateUserProfile(input: $input) {
      id
      email
      firstName
      lastName
      phone
      sex
      shirtSize
      interestedEvents
      referredBy
      approved
      paid
      joinDate
      lastPaidDate
    }
  }
`;

// Current user query
export const GET_CURRENT_USER = `
  query GetCurrentUser {
    me {
      id
      email
      name
      approved
      joinDate
    }
  }
`;

// Execute a GraphQL mutation with authorization
export const executeMutation = async (mutation, variables) => {
  try {
    // Get the authentication token
    const storedToken = sessionStorage.getItem("cognitoIdToken");

    if (!storedToken) {
      throw new Error("Authentication required");
    }

    const response = await fetch(
      process.env.REACT_APP_GRAPHQL_API_URL ||
        "https://nkeefgsaz2.execute-api.us-east-1.amazonaws.com/prod/graphql",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${storedToken}`,
        },
        body: JSON.stringify({
          query: mutation,
          variables,
        }),
      }
    );

    const result = await response.json();

    if (result.errors) {
      throw new Error(result.errors.map((e) => e.message).join(", "));
    }

    return result.data;
  } catch (error) {
    console.error("GraphQL mutation error:", error);
    throw error;
  }
};

// Execute a GraphQL query with authorization
export const executeQuery = async (query, variables) => {
  try {
    // Get the authentication token
    const storedToken = sessionStorage.getItem("cognitoIdToken");

    if (!storedToken) {
      throw new Error("Authentication required");
    }

    const response = await fetch(
      process.env.REACT_APP_GRAPHQL_API_URL ||
        "https://nkeefgsaz2.execute-api.us-east-1.amazonaws.com/prod/graphql",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${storedToken}`,
        },
        body: JSON.stringify({
          query,
          variables,
        }),
      }
    );

    const result = await response.json();

    if (result.errors) {
      throw new Error(result.errors.map((e) => e.message).join(", "));
    }

    return result.data;
  } catch (error) {
    console.error("GraphQL query error:", error);
    throw error;
  }
};

// User profile management functions
export const updateUserProfile = async (profileData) => {
  try {
    const result = await executeMutation(UPDATE_USER_PROFILE, {
      input: profileData,
    });
    return result.updateUserProfile;
  } catch (error) {
    console.error("Error updating user profile:", error);
    throw error;
  }
};

export default client;
